import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { navigate } from '../lib/router.js'
import { TEMPLATES, projectFromTemplate, defaultParams } from '../model/templates.js'
import { MATERIALS, materialOf } from '../model/materials.js'
import { summarize } from '../model/edit.js'
import { Icon, Logo, Modal, NumField, Field, Select, Seg, Toasts, toast } from '../ui/controls.jsx'
import { dims } from '../model/format.js'
import { PRINT_TEMPLATES, printProjectFromTemplate } from '../print/templates.js'

// Start page: all projects as cards with their last 3D view, and "Neues Projekt" with the
// templates (Regal, Hocker, Rahmen, Kiste, Leuchten …) and their parameters.

const fmtDate = (t) => new Date(t).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

const KINDS = {
  wood: { title: 'Holzwerkstatt', path: '/', tag: 'Holzprojekte planen', desc: 'Regal, Hocker, Leuchte … in 3D planen: jedes Teil mit Maß und Winkel, die Holzverbindungen wählen, als Explosionszeichnung prüfen und mit der Zuschnittliste in die Werkstatt.' },
  print: { title: '3D-Werkstatt', path: '/3d', tag: 'Teile für den 3D-Drucker', desc: 'Gehäuse und Halter aus Maßen modellieren, als Explosionszeichnung und auf dem Druckbett prüfen, als STL herunterladen und in Orca slicen.' },
}

export default function Projects({ kind = 'wood' }) {
  const K = KINDS[kind]
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: ['projects'], queryFn: () => api('/projects') })
  const [creating, setCreating] = useState(false)
  const [menu, setMenu] = useState(null)
  useEffect(() => { document.title = kind === 'print' ? '3D-Werkstatt · Mori' : 'Mori' }, [kind])
  const list = data?.filter((p) => (p.summary?.kind === 'print') === (kind === 'print'))
  const open = (p) => navigate(kind === 'print' ? `/3d/${p.id}` : `/p/${p.id}`)
  useEffect(() => {
    if (!menu) return
    const off = () => setMenu(null)
    window.addEventListener('click', off)
    return () => window.removeEventListener('click', off)
  }, [menu])

  const remove = async (p) => {
    await api(`/projects/${p.id}`, { method: 'DELETE' })
    qc.invalidateQueries({ queryKey: ['projects'] })
    toast(`„${p.name}“ gelöscht.`)
  }
  const duplicate = async (p) => {
    await api(`/projects/${p.id}/duplicate`, { method: 'POST' })
    qc.invalidateQueries({ queryKey: ['projects'] })
  }

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <header className="flex items-center gap-3 px-6 flex-none" style={{ height: 60, borderBottom: '1px solid var(--line)', background: 'var(--panel-low)' }}>
        <Logo size={30} word />
        <nav className="flex items-center gap-1 ml-2">
          {Object.entries(KINDS).map(([k, x]) => (
            <button key={k} type="button" className="ws-tab-lite" aria-current={k === kind} onClick={() => navigate(x.path)}
              style={{ padding: '6px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: k === kind ? 'rgba(176,206,174,0.12)' : 'transparent', color: k === kind ? 'var(--sage)' : 'var(--muted)' }}>
              <Icon name={k === 'print' ? 'view_in_ar' : 'carpenter'} size={16} style={{ verticalAlign: '-3px', marginRight: 6 }} />{x.title}
            </button>
          ))}
        </nav>
        <div className="flex-1" />
        <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="add" size={17} />Neues Projekt</button>
      </header>
      <div className="px-6 py-8 mx-auto" style={{ maxWidth: 1480 }}>
        <div className="flex flex-col gap-1 mb-6">
          <h1 className="font-headline font-bold text-on-surface" style={{ fontSize: '1.75rem', letterSpacing: '-0.03em' }}>{K.title}</h1>
          <p className="ds-text-desc">{K.desc}</p>
        </div>
        {isLoading && <p className="ds-text-desc">Lade …</p>}
        {error && <p className="text-error" style={{ fontSize: 13 }}>Projekte konnten nicht geladen werden.</p>}
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          <button type="button" onClick={() => setCreating(true)} className="flex flex-col items-center justify-center gap-2 text-outline"
            style={{ minHeight: 250, borderRadius: 14, border: '1.5px dashed rgba(255,255,255,0.12)', background: 'transparent', cursor: 'pointer' }}>
            <Icon name="add_circle" size={30} style={{ color: 'var(--sage)' }} />
            <span className="font-headline font-semibold text-on-surface-variant" style={{ fontSize: 14 }}>Neues Projekt</span>
            <span style={{ fontSize: 12 }}>aus einer Vorlage oder leer</span>
          </button>
          {list?.map((p) => (
            <div key={p.id} className="relative group flex flex-col bg-surface-container overflow-hidden" style={{ borderRadius: 14, border: '1px solid var(--line)' }}>
              <button type="button" onClick={() => open(p)} className="text-left flex flex-col" style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                <div className="viewport-ground flex items-center justify-center" style={{ aspectRatio: '4 / 3', width: '100%' }}>
                  {p.thumb ? <img src={p.thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon name={kind === 'print' ? 'view_in_ar' : 'carpenter'} size={40} style={{ color: 'var(--faint)' }} />}
                </div>
                <div className="flex flex-col gap-1 px-4 py-3">
                  <span className="font-headline font-bold text-on-surface truncate" style={{ fontSize: 14.5 }}>{p.name}</span>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-outline whitespace-nowrap" style={{ fontSize: 11.5 }}>
                    {p.summary?.material && <span className="inline-flex items-center gap-1"><span style={{ width: 8, height: 8, borderRadius: 2, background: materialOf(p.summary.material).color }} />{materialOf(p.summary.material).name}</span>}
                    {p.summary?.parts != null && <span>· {p.summary.parts} Teile</span>}
                    {p.summary?.size && <span className="font-mono">· {dims(p.summary.size)}</span>}
                    {p.summary?.qty > 1 && <span>· {p.summary.qty} Stück</span>}
                    {p.summary?.cost != null && <span className="font-mono" title="Kosten ca. je Stück (Material, Kaufteile, Strom)">· ca. {p.summary.cost} €{p.summary?.qty > 1 ? ' je Stück' : ''}</span>}
                  </span>
                  <span className="text-outline-faint" style={{ fontSize: 11 }}>geändert {fmtDate(p.updated_at)}</span>
                </div>
              </button>
              <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {kind !== 'print' && <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(`/p/${p.id}/werkstatt`)} title="Werkstatt-Ansicht"><Icon name="handyman" size={14} /></button>}
                <button type="button" className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setMenu(menu === p.id ? null : p.id) }} title="Mehr"><Icon name="more_horiz" size={14} /></button>
              </div>
              {menu === p.id && (
                <div className="absolute right-2 top-10 z-10 inner-panel bg-surface-container-high py-1 flex flex-col" style={{ border: '1px solid var(--line-strong)', boxShadow: '0 12px 30px rgba(0,0,0,0.5)', minWidth: 160 }}>
                  <button type="button" className="row-btn" style={{ fontSize: 12.5 }} onClick={() => duplicate(p)}><Icon name="content_copy" size={15} />Duplizieren</button>
                  <button type="button" className="row-btn" style={{ fontSize: 12.5, color: 'var(--error)' }} onClick={() => remove(p)}><Icon name="delete" size={15} />Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {kind === 'print' ? <NewPrint open={creating} onClose={() => setCreating(false)} /> : <NewProject open={creating} onClose={() => setCreating(false)} />}
      <Toasts />
    </div>
  )
}

function NewPrint({ open, onClose }) {
  const [tid, setTid] = useState(PRINT_TEMPLATES[0].id)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const create = async () => {
    setBusy(true)
    try {
      const data = printProjectFromTemplate(tid, name.trim() || undefined)
      const r = await api('/projects', { method: 'POST', body: { name: data.name, data, summary: { kind: 'print', template: tid } } })
      onClose()
      navigate(`/3d/${r.id}`)
    } catch (e) {
      toast(`Anlegen fehlgeschlagen: ${e.message}`, 'error')
    } finally { setBusy(false) }
  }
  const tpl = PRINT_TEMPLATES.find((t) => t.id === tid)
  return (
    <Modal open={open} onClose={onClose} title="Neues 3D-Druck-Projekt" subtitle="Maße passt du danach an deine Bauteile an" width={640}
      footer={<>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
        <button type="button" className="btn btn-primary" onClick={create} disabled={busy}><Icon name={busy ? 'progress_activity' : 'arrow_forward'} size={16} className={busy ? 'animate-spin' : ''} />Anlegen</button>
      </>}>
      <div className="flex flex-col gap-3">
        {PRINT_TEMPLATES.map((t) => (
          <button key={t.id} type="button" onClick={() => setTid(t.id)} className="flex items-start gap-3 p-3 text-left"
            style={{ borderRadius: 12, cursor: 'pointer', background: t.id === tid ? 'rgba(176,206,174,0.09)' : 'rgba(255,255,255,0.025)', border: `1px solid ${t.id === tid ? 'rgba(176,206,174,0.45)' : 'rgba(255,255,255,0.06)'}` }}>
            <Icon name={t.icon} size={24} style={{ color: 'var(--sage)' }} />
            <span className="flex flex-col gap-1"><span className="font-headline font-semibold text-on-surface" style={{ fontSize: 13.5 }}>{t.name}</span><span className="text-outline" style={{ fontSize: 12 }}>{t.desc}</span></span>
          </button>
        ))}
        <Field label="Name"><input className="ds-input" placeholder={tpl.name} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      </div>
    </Modal>
  )
}

function NewProject({ open, onClose }) {
  const [tid, setTid] = useState('regal')
  const tpl = TEMPLATES.find((t) => t.id === tid)
  const [params, setParams] = useState(() => defaultParams(TEMPLATES.find((t) => t.id === 'regal')))
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const pick = (t) => { setTid(t.id); setParams(defaultParams(t)) }
  const create = async () => {
    setBusy(true)
    try {
      const data = projectFromTemplate(tid, params, name.trim() || undefined)
      const r = await api('/projects', { method: 'POST', body: { name: data.name, data, summary: summarize(data) } })
      onClose()
      navigate(`/p/${r.id}`)
    } catch (e) {
      toast(`Anlegen fehlgeschlagen: ${e.message}`, 'error')
    } finally { setBusy(false) }
  }
  return (
    <Modal open={open} onClose={onClose} title="Neues Projekt" subtitle="Eine Vorlage ist nur der Anfang — danach ist jedes Teil frei änderbar" width={940}
      footer={<>
        <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
        <button type="button" className="btn btn-primary" onClick={create} disabled={busy}><Icon name={busy ? 'progress_activity' : 'arrow_forward'} size={16} className={busy ? 'animate-spin' : ''} />Anlegen</button>
      </>}>
      <div className="grid gap-5" style={{ gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1fr)' }}>
        <div className="grid grid-cols-2 gap-2 content-start">
          {TEMPLATES.map((t) => (
            <button key={t.id} type="button" onClick={() => pick(t)} className="flex flex-col items-start gap-1.5 p-3 text-left"
              style={{ borderRadius: 12, cursor: 'pointer', background: t.id === tid ? 'rgba(176,206,174,0.09)' : 'rgba(255,255,255,0.025)', border: `1px solid ${t.id === tid ? 'rgba(176,206,174,0.45)' : 'rgba(255,255,255,0.06)'}` }}>
              <Icon name={t.icon} size={22} style={{ color: t.id === tid ? 'var(--sage)' : 'var(--wood)' }} />
              <span className="font-headline font-semibold text-on-surface" style={{ fontSize: 13 }}>{t.name}</span>
              <span className="text-outline" style={{ fontSize: 11.5, lineHeight: 1.45 }}>{t.desc}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-col gap-3">
          <Field label="Name"><input className="ds-input" placeholder={tpl.name} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            {tpl.params.map((p) => (
              <Field key={p.key} label={p.label}>
                {p.type === 'material' ? (
                  <Select value={params[p.key]} onChange={(v) => setParams({ ...params, [p.key]: v })} options={MATERIALS.filter((m) => !m.noGrain).map((m) => ({ value: m.id, label: m.name }))} />
                ) : p.type === 'select' ? (
                  <Select value={params[p.key]} onChange={(v) => setParams({ ...params, [p.key]: v })} options={p.options} />
                ) : p.type === 'bool' ? (
                  <Seg small value={params[p.key] ? 'ja' : 'nein'} onChange={(v) => setParams({ ...params, [p.key]: v === 'ja' })} options={[{ value: 'ja', label: 'Ja' }, { value: 'nein', label: 'Nein' }]} />
                ) : (
                  <NumField unit={p.unit === 'Stk' ? '' : p.unit} value={params[p.key]} min={p.min} max={p.max} onChange={(v) => setParams({ ...params, [p.key]: v })} />
                )}
              </Field>
            ))}
          </div>
          <p className="text-outline-faint" style={{ fontSize: 11.5, lineHeight: 1.55 }}>Alle Maße in Millimetern. Verbindungen setzt die Vorlage so, wie man das Stück üblicherweise baut — du kannst jede einzeln ändern.</p>
        </div>
      </div>
    </Modal>
  )
}

